import { forwardRef, Ref, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Colors } from '~/themes/colors';
import { Column } from '../Column';
import { Row } from '../Row';
import { Text } from '../Text';
import { ChipOption } from './ChipOption';
import { PillOption } from './PillOption';
import { IMultiSelectGroupProps, TOptionValue } from './Types';
import { createValueRecord, toSortedArray } from './Utils';

const Component = <T extends string | number = string>(
  {
    isEnableSelectAll = false,
    noWrapOptions = false,
    options,
    value: controlledValue,
    onChange,
    variant = 'checkbox',
  }: IMultiSelectGroupProps<T>,
  ref: Ref<HTMLDivElement>,
) => {
  const [internalValue, setInternalValue] = useState<Record<T, string>>({} as Record<T, string>);

  const optionsMap = useMemo(
    () => new Map(options.map((opt) => [opt.value, opt.label])),
    [options],
  );

  const isControlled = controlledValue !== undefined;
  const currentValue = isControlled
    ? createValueRecord(controlledValue, optionsMap)
    : internalValue;

  const { current: wasControlled } = useRef(isControlled);
  useEffect(() => {
    if (!import.meta.env.PROD && wasControlled !== isControlled) {
      console.warn(
        `Warning: MultiSelectGroup is changing from ${wasControlled ? 'controlled' : 'uncontrolled'} to ${isControlled ? 'controlled' : 'uncontrolled'}. ` +
          'Elements should not switch from controlled to uncontrolled (or vice versa).',
      );
    }
  }, [isControlled, wasControlled]);

  const handleClick = useCallback(
    (value: T, isChecked: boolean) => {
      const label = optionsMap.get(value);
      if (!label) return;

      const toggleOption = (record: Record<T, string>) => {
        const copy = { ...record };
        if (isChecked) {
          copy[value] = label;
        } else {
          delete copy[value];
        }
        return copy;
      };

      if (isControlled) {
        const updated = toggleOption(createValueRecord(controlledValue!, optionsMap));
        onChange?.(toSortedArray(updated) as TOptionValue<T>[]);
      } else {
        setInternalValue((prev) => {
          const updated = toggleOption(prev);
          onChange?.(toSortedArray(updated) as TOptionValue<T>[]);
          return updated;
        });
      }
    },
    [isControlled, controlledValue, optionsMap, onChange],
  );

  const handleSelectAll = useCallback(() => {
    const allOptionsRecord = options.reduce(
      (acc, opt) => {
        acc[opt.value] = opt.label;
        return acc;
      },
      {} as Record<T, string>,
    );

    if (isControlled) {
      onChange?.(toSortedArray(allOptionsRecord) as TOptionValue<T>[]);
    } else {
      setInternalValue(allOptionsRecord);
      onChange?.(toSortedArray(allOptionsRecord) as TOptionValue<T>[]);
    }
  }, [isControlled, options, onChange]);

  return (
    <Column ref={ref}>
      {isEnableSelectAll && (
        <Text
          bold
          color={Colors.secondary_500}
          cursor="pointer"
          data-testid="multi-select-group.select-all-button"
          fontSize={18}
          marginBottom={4}
          marginLeft={16}
          width="fit-content"
          role="button"
          tabIndex={0}
          onClick={handleSelectAll}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleSelectAll();
            }
          }}
        >
          Select All
        </Text>
      )}
      <Row flexWrap={noWrapOptions ? 'nowrap' : 'wrap'} gap={8}>
        {options.map((it) =>
          variant === 'pill' ? (
            <PillOption
              isChecked={!!currentValue[it.value]}
              key={String(it.value)}
              label={it.label}
              value={it.value}
              onClick={handleClick}
            />
          ) : (
            <ChipOption
              isChecked={!!currentValue[it.value]}
              key={String(it.value)}
              label={it.label}
              value={it.value}
              onClick={handleClick}
            />
          ),
        )}
      </Row>
    </Column>
  );
};

/**
 * A flexible multi-select component that allows users to select multiple options from a list using chip-style buttons.
 * Supports both controlled and uncontrolled modes with optional "Select All" functionality.
 *
 * @example
 * ```tsx
 * // Uncontrolled mode
 * <MultiSelectGroup
 *   options={[
 *     { value: '1', label: 'Option 1' },
 *     { value: '2', label: 'Option 2' }
 *   ]}
 *   onChange={(selected) => console.log(selected)}
 * />
 *
 * // Controlled mode with Select All
 * <MultiSelectGroup
 *   options={options}
 *   value={selectedValues}
 *   onChange={setSelectedValues}
 *   isEnableSelectAll={true}
 * />
 * ```
 *
 * @template T - The type of the option values (string | number)
 */
export const MultiSelectGroup = forwardRef(Component) as <T extends string | number = string>(
  props: IMultiSelectGroupProps<T> & { ref?: Ref<HTMLDivElement> },
) => ReturnType<typeof Component>;
